import { CLIENT_TIMEOUT_MS, reviewEndpoint } from '~/config/worker'
import type { GeminiFailure } from '~/contracts/gemini/errors'
import type { GeminiRequestPayload } from '~/contracts/gemini/request'
import { prepareGeminiRequest } from '~/contracts/gemini/request'
import type { GeminiProposal, GeminiResponse, RefusedRule } from '~/contracts/gemini/response'
import { SOLA_TOKEN_HEADER } from '~/contracts/gemini/version'
import type { SolaDatabase } from '~/storage/db'
import { getConnection } from '~/storage/repositories/connectionRepository'

/**
 * Asking the Worker for a second opinion (D-093, D-094, D-095).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  EVERY OUTCOME IS A VALUE. NOTHING HERE THROWS
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  Twelve failure categories, and the screen renders each as a state. A failed
 *  review is an ordinary answer — "no proposal, and here is why" — not an
 *  exception, which is the project-wide rule that a failure must never be
 *  reported as a success and must never arrive as a crash.
 *
 *  **Three of the twelve never reach the network**, and that is the point of
 *  checking them here: no Worker configured, no token stored, and no
 *  connection. Each is knowable before spending anything, and each has an
 *  honest name of its own rather than being collapsed into "unavailable".
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  OFFLINE DEGRADES TO NOTHING, AND NOTHING IS QUEUED
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  D-093 §4: no queue, no background sync, no retry-later, and **no record
 *  written for a call that never happened.** A queued request would leave at a
 *  moment the user did not confirm, which contradicts the mandatory
 *  per-request preview, and would review a stale week as though it were current.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THE RETRY NEVER CHARGES TWICE, BECAUSE IT IS NOT THIS SIDE'S DECISION
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  **The client does not retry at all.** The Worker already retries the two
 *  clearly transient provider failures internally, counting the user's action
 *  once rather than its own attempts. A client-side retry on top would spend a
 *  second slot on the meter for the same intention — the meter counts actions,
 *  and asking twice for one answer is one action.
 *
 *  A transient failure is reported as retryable so the screen can offer the
 *  user the choice. That is a new action when they take it, and it is theirs.
 */

export type ReviewOutcome =
  | { readonly ok: true; readonly proposal: GeminiProposal }
  | {
      readonly ok: false
      readonly failure: GeminiFailure
      /** Which of D-094's five prose rules was broken, where that is why. */
      readonly refusedRule?: RefusedRule
      /** Whether offering "try again" is honest for this failure. */
      readonly retryable: boolean
    }

export interface ReviewDeps {
  /* Injected so tests drive every path without a network, and so the browser
     suite can assert the offline state without turning off the machine's
     networking. Defaults are the real ones, so production wiring passes none. */
  readonly fetchImpl?: typeof fetch
  readonly isOnline?: () => boolean
}

function failed(failure: GeminiFailure, refusedRule?: RefusedRule): ReviewOutcome {
  return {
    ok: false,
    failure,
    ...(refusedRule === undefined ? {} : { refusedRule }),
    /* Only the two failures that mean the call may simply not have happened.
       `geminiRateLimited` is deliberately excluded even though it looks
       transient: retrying into a rate limit turns one refusal into several and
       each attempt still spends a slot. */
    retryable: failure === 'geminiUnavailable' || failure === 'requestTimedOut',
  }
}

/**
 * Whether a second opinion can be asked for at all, and why not.
 *
 * Separated from `requestReview` so the screen can render a disabled entry point
 * with an honest reason **without sending anything**. D-093 §4 requires the
 * control to stay visible and disabled rather than disappear, because a screen
 * whose shape depends on connectivity teaches the user the feature is
 * unreliable.
 */
export async function reviewAvailability(
  db: SolaDatabase,
  deps: ReviewDeps = {},
): Promise<
  { readonly available: true } | { readonly available: false; readonly failure: GeminiFailure }
> {
  if (reviewEndpoint() === undefined) {
    /* No Worker in this build. A fact about the build, not a fault — rendered
       as a clean state, never as an outage (D-095 §4). */
    return { available: false, failure: 'featureDisabled' }
  }

  const online = deps.isOnline ?? (() => navigator.onLine)
  if (!online()) {
    return { available: false, failure: 'geminiUnavailable' }
  }

  const connection = await getConnection(db, 'solaWorker')
  if (!connection.ok || connection.value === undefined) {
    /* The same state the Worker would report for a wrong token, decided here so
       no request is spent discovering something already known. The screen points
       at the token field in Settings (D-095 §1). */
    return { available: false, failure: 'authenticationRefused' }
  }

  return { available: true }
}

/**
 * Send one request, and answer with an outcome.
 *
 * The payload arrives already built (`services/geminiPayload.ts`) and already
 * previewed. **This function does not build one**, so there is no path where a
 * request is sent without the user having seen exactly what it contains.
 */
export async function requestReview(
  db: SolaDatabase,
  payload: GeminiRequestPayload,
  deps: ReviewDeps = {},
): Promise<ReviewOutcome> {
  const availability = await reviewAvailability(db, deps)
  if (!availability.available) return failed(availability.failure)

  const connection = await getConnection(db, 'solaWorker')
  if (!connection.ok || connection.value === undefined) {
    return failed('authenticationRefused')
  }

  /* The single gate from payload to bytes. A caller cannot bypass it by
     assembling its own JSON, which is the property `serializeWeeklyReport` has
     for exports — and the reason the forbidden-content guard cannot be skipped. */
  const prepared = prepareGeminiRequest(payload)
  if (!prepared.ok) {
    /* Refused before anything leaves the device. The findings are deliberately
       not surfaced: they name paths inside a payload that was just judged unfit
       to send, and a screen is not the place to enumerate them. */
    return failed(prepared.failure)
  }

  const endpoint = reviewEndpoint()
  if (endpoint === undefined) return failed('featureDisabled')

  const doFetch = deps.fetchImpl ?? fetch
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), CLIENT_TIMEOUT_MS)

  try {
    const response = await doFetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        /* The pasted token, exactly as stored, in the header the shared contract
           names. The Worker compares the literal string (D-095 §1).

           **This was `Authorization: Bearer <token>` for three phases and every
           real request was refused because of it** — wrong header name, a prefix
           the literal comparison could never match, and a header the Worker's
           CORS allowlist did not permit, so a browser preflight would have
           blocked it before it was sent. The name is read from the contract now
           rather than written here, which is what makes the two ends unable to
           disagree again. */
        [SOLA_TOKEN_HEADER]: connection.value.token,
      },
      body: prepared.body,
      signal: controller.signal,
    })

    return readResponse(await response.json().catch(() => undefined), response.status)
  } catch (error) {
    /* An abort is the timeout; anything else is a network that did not carry the
       request. Both mean the call may simply not have happened, which is why
       both are the retryable pair. */
    const timedOut = error instanceof Error && error.name === 'AbortError'
    return failed(timedOut ? 'requestTimedOut' : 'geminiUnavailable')
  } finally {
    clearTimeout(timer)
  }
}

/**
 * Read the Worker's answer, trusting its vocabulary and nothing else.
 *
 * **The status code is a fallback, not the source of truth.** The Worker answers
 * in Sola's own contract — `{ok, failure}` — and a body that says so is the
 * authority. The status is consulted only when the body is unreadable, which is
 * the case where something between the two ends answered instead of the Worker:
 * a proxy, a captive portal, or an origin that returns its own HTML for an
 * unknown path. The deployed Pages host already does exactly that, so this is a
 * failure mode this project has met.
 */
function readResponse(body: unknown, status: number): ReviewOutcome {
  if (typeof body === 'object' && body !== null && 'ok' in body) {
    const answer = body as GeminiResponse
    if (answer.ok) return { ok: true, proposal: answer.proposal }
    return failed(answer.failure, answer.refusedRule)
  }

  /* No usable body. Map the status honestly rather than inventing a category:
     an unreadable answer is `invalidGeminiResponse` only when it came from a
     successful-looking request, and otherwise the transport is what failed. */
  if (status === 401 || status === 403) return failed('authenticationRefused')
  if (status === 429) return failed('rateLimitReached')
  if (status >= 500) return failed('geminiUnavailable')
  return failed('invalidGeminiResponse')
}
